import { AppThunkAction } from "../../store/index";
import { Reducer } from "redux";
import { Api, getSessionInfo } from "../../api/Api";
import { AuthAction } from "./authActions";
import * as checkRequestActions from "../checkRequests/checkRequestReducer";
import * as tripActions from "../trips/tripReducer";
import { completeRedirectSignIn, signOut, startSignIn } from "./msal";
import { IUser } from "../../components/index";
type KnownAction = AuthAction;

const api = new Api();

export interface IAuthState {
  isLoggingIn: boolean;
  hasValidSession: boolean;
  users: any[];
  currentUser: IUser | null;
  currentUserHasProfile: boolean;
  primaryManagerEmail: string;
  primaryManagerName: string;
  primaryManager: any;
}
export const unloadedState: IAuthState = {
  isLoggingIn: false,
  hasValidSession: false,
  users: [],
  currentUser: null,
  currentUserHasProfile: false,
  primaryManagerEmail: "",
  primaryManagerName: "",
  primaryManager: null,
};

export enum AuthActionTypes {
  REDIRECT_TO_AZURE_AD = "REDIRECT_TO_AZURE_AD",
  GET_AZURE_AUTHENTICATION_TOKEN = "GET_AZURE_AUTHENTICATION_TOKEN",
}
export interface RedirectToAzureADAction {
  type: AuthActionTypes.REDIRECT_TO_AZURE_AD;
}
export interface GetAzureAuthenticationTokenAction {
  type: AuthActionTypes.GET_AZURE_AUTHENTICATION_TOKEN;
}
export const actionCreators = {
  initializeApp: (): AppThunkAction<any> => async (dispatch, getState) => {
    await dispatch(actionCreators.getCurrentUser());

    // if we couldn't resolve a current user (no/invalid session), stop here to avoid
    // downstream calls that assume currentUser is set (e.g. currentUser.id).
    if (!getState().auth.currentUser) {
      return;
    }

    await dispatch(tripActions.actionCreators.getMiscExpenseCategories());
    await dispatch(tripActions.actionCreators.getCurrencies());
    await dispatch(tripActions.actionCreators.getCategories());
    await dispatch(tripActions.actionCreators.getTrips());
    await dispatch(checkRequestActions.actionCreators.getCheckRequests());
    await dispatch(
      checkRequestActions.actionCreators.getCheckRequestsToApprove(),
    );
  },
  redirectToAzureAD: (): AppThunkAction<KnownAction> => async (dispatch) => {
    dispatch({
      type: "GET_AZURE_AUTHENTICATION_TOKEN",
      isLoggingIn: true,
    });
    dispatch({
      type: "REDIRECT_TO_AZURE_AD",
    });    
    await startSignIn();
  },
  completeMsalSignIn: (): AppThunkAction<KnownAction> => async () => {
      const tokens = await completeRedirectSignIn();
      if (tokens) {
        const response = await api.getMsalAuthenticationToken(
          tokens.idToken,
          tokens.graphAccessToken,
        );
        window.sessionStorage.setItem("expense_token", response.token);
      }
    },
  logout: (): AppThunkAction<KnownAction> => async (dispatch) => {
    window.sessionStorage.removeItem("expense_token");
    window.sessionStorage.removeItem("profile");
    window.sessionStorage.removeItem("appUser");
    window.sessionStorage.removeItem("profileImage");
    dispatch({
      type: "CHECK_VALID_SESSION",
      hasValidSession: false,
    });
    await signOut();
  },
  checkValidSession: (): AppThunkAction<KnownAction> => (dispatch) => {
    let sessionToken = window.sessionStorage.getItem("expense_token");
    dispatch({
      type: "CHECK_VALID_SESSION",
      hasValidSession: Boolean(sessionToken),
    });
  },
  getCurrentUser: (): AppThunkAction<any> => async (dispatch, getState) => {
    let currentUser = getSessionInfo();
    if (!currentUser) {
      return;
    }

    dispatch({ type: "CHECK_VALID_SESSION", hasValidSession: true });

    // get the user's profile info from SQL database and roll into token.
    try {
      await api
        .getCurrentUser()
        .then((result: any) => {
          let appUser = result.appUser;

          // combine their AD profile and SQL profile:
          currentUser.id = appUser.id; // id on SQL server.
          currentUser.azureId = ""; // id on Azure AD.

          currentUser.primaryManagerEmail = appUser.primaryManagerEmail;
          currentUser.companyNumber = parseInt(appUser.companyNumber);
          currentUser.itCostCenter = appUser.itCostCenter;

          // set the employeeid to be the users vendor number.
          currentUser.vendorNumber = appUser.vendorNumber;
          currentUser.isAuditor = appUser.isAuditor;
          currentUser.isUSemployee = appUser.isUSemployee;

          dispatch({
            type: "GET_CURRENT_USER_SUCCESS",
            currentUser: currentUser,
            currentUserHasProfile:
              appUser.companyNumber &&
              appUser.primaryManagerEmail &&
              appUser.vendorNumber &&
              appUser.itCostCenter,
          });
        });
    } catch (error) {}
  },
  loadUserList:
    (inputValue, callback): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      if (!inputValue) {
        return;
      }
      dispatch({
        type: "GET_ALL_USERS",
        users: [],
      });
      api
        .getAutocompleteUser(inputValue)
        .then((response) => response.json())
        .then((data) => {
          const users = data.value;
          // console.log('Fetched users: ', users);
          return users;
        })
        .then((results: any) => {
          let users: any = [];
          for (let i = 0; i < results.length; i++) {
            // console.log(results[i].displayName);
              if (
                  results[i].mail.includes("schott.com")          
              // results[i].displayName === 'Hakan Gerdan' ||
              // results[i].displayName === 'Adem Yuksel' ||
              // results[i].displayName === 'Flavia Painelli'
            ) {
              if (!results[i].employeeId) {
                // console.log(results[i].displayName + ' does not have an employeeId');
                continue;
              }
              users.push({
                label: results[i].displayName,
                value: results[i].mail,
                vendorNumber: results[i].employeeId,
              });
            }
          }

          // dead code - filtering is now done server side in getAutocompleteUser, but leaving this here in case we need to do additional client side filtering in the future.
          users &&
            users.filter((i: any) =>
              i.label.toLowerCase().includes(inputValue.toLowerCase()),
            );

          dispatch({
            type: "GET_ALL_USERS",
            users: users,
          });
        })
        .catch((error) => console.log("Error fetching users: ", error));
    },
};

// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const reducer: Reducer<IAuthState> = (
  state: IAuthState | undefined,
  action: KnownAction,
): IAuthState => {
  switch (action.type) {
    case "GET_AZURE_AUTHENTICATION_TOKEN":
      return {
        ...state,
        isLoggingIn: action.isLoggingIn,
      };
    case "REDIRECT_TO_AZURE_AD":
      return {
        ...state,
        isLoggingIn: true,
      };
    case "CHECK_VALID_SESSION":
      return {
        ...state,
        hasValidSession: action.hasValidSession,
      };
    case "GET_ALL_USERS":
      return {
        ...state,
        users: action.users,
      };
    case "GET_CURRENT_USER_SUCCESS":
      return {
        ...state,
        currentUser: action.currentUser,
        currentUserHasProfile: action.currentUserHasProfile,
      };
    case "HANDLE_PRIMARY_CHANGE":
      return {
        ...state,
        primaryManagerEmail: action.primaryManagerEmail,
        primaryManagerName: action.primaryManagerName,
        primaryManager: action.primaryManager,
      };
    default:
      break;
  }

  // For unrecognized actions (or in cases where actions have no effect), must return the existing state
  //  (or default initial state if none was supplied)
  return state || unloadedState;
};
